
import type {
	GNCPeriod,
	IAPIJobForUser,
	IAPIJobPreviewForUser,
	IAPIJobQuestion,
	IAPILocation,
	IAPIMatchForJobSeekerListEntry,
	IAPIOwnerSnippet,
	IAPIPageJobDetail
} from '@hokify/common';
import type { PropType } from 'vue';
import { defineComponent } from 'vue';
import { piniaAvailable } from '../helpers/piniaStoreAvailable';
import SharingModal from './SharingModal.vue';
import { getJobSharingInfo, ISharingInfo } from '../helpers/sharing-modal';
import type { IRecentJobsearch } from '../types/recentJobsearch';
import { getTextForSearches } from '../helpers/jobsearch';
import { fromNow } from '../helpers/datehelpers/from-now';
import { lsTest } from '../helpers/localstorage';
import { truncateFct } from '../helpers/truncate';
import BeautifyText from './BeautifyText.vue';
import JobFacts from './JobFacts.vue';
import HokifyBenefits from './HokifyBenefits.vue';
import JobHeaderImage from './JobHeaderImage.vue';
import HokLink from './HokLink.vue';
import { faqData } from '../data/short-faq';
import TransitionExpand from './TransitionExpand.vue';
import HokImg from './HokImg.vue';

function isHTMLElement(element: any): element is HTMLElement {
	return element && element instanceof HTMLElement;
}

type MapState = 'image' | 'map';
export default defineComponent({
	name: 'JobDetailNew',
	methods: {
		fromNow,
		getTextForSearches,
		truncateFct,
		registerIntersectionObserver() {
			const container = document.querySelector('#overflow-container');
			this.observer = new IntersectionObserver(this.isVisible, { root: container });
			this.observeElements();
		},
		observeElements() {
			const element = document.querySelector('#jobdetail-header-desktop');
			if (element) {
				this.observer?.observe(element);
			}
		},
		isVisible(entries) {
			this.showBar = !entries.some(entry => entry.isIntersecting);
		},
		handleClickContact() {
			if (this.job?.selectedCandidateInfo?.relatedMatchId && this.isInContactWithCompany) {
				window.location.href = `/match/${this.job.selectedCandidateInfo?.relatedMatchId}`;
			} else {
				this.$emit('acts-invite', 'accepted');
			}
		},
		setMapState(state: MapState) {
			this.mapState = state;
			if (this.jobRegion === 'de') {
				this.$emit('show-map-dropdown');
			} else {
				this.$emit('show-map-image');
			}
		},
		revealMap() {
			if (this.mapState !== 'map') {
				this.setMapState('map');
			}
			this.showMap = !this.showMap;
		},
		scrollToGnc() {
			this.showGnc = true;
			const element = this.$refs.grossNetCalculatorRef;
			if (isHTMLElement(element)) {
				element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		},
		handleNoInterest() {
			if (this.hasRejectedContactRequest) {
				this.$snack.danger({
					text: 'Du hast bereits "kein Interesse" kommuniziert, kannst aber dennoch Kontakt aufnehmen.'
				});
			} else {
				this.$emit('acts-invite', 'declined');
			}
		},
		shareJob() {
			if (!this.isPwa) {
				this.sharingInfo = getJobSharingInfo(this.job);
				this.$modal.show('sharingmodal');
			}
			this.$emit('send-job', 'cta-button-right');
		},
		onJobChanged() {
			this.showGnc = false;
			this.showMap = false;
			this.mapState = 'image';
		},
		updateRemuneration(updatedRemuneration) {
			this.remuneration = updatedRemuneration;
		},
		updatePeriod(updatedPeriod) {
			this.period = updatedPeriod;
		},
		newSearchViaTag(jobField: string) {
			this.$emit('new-search-via-tag', jobField);
		}
	},
	components: {
		HokImg,
		HokLink,
		BeautifyText,
		JobFacts,
		JobHeaderImage,
		HokifyBenefits,
		TransitionExpand,
		SharingModal,
		JobList: () => import('./JobList.vue'),
		JobContactPerson: () => import('./JobContactPerson.vue'),
		HokMarkerMap: () => import('./HokMarkerMap.vue'),
		JobRequiredFields: () => import('./JobRequiredFields.vue'),
		CallToAction: () => import('./CallToAction.vue'),
		FAQ: () => import('./FAQ.vue'),
		GrossNetCalculator: () => import('./GrossNetCalculator.vue')
	},
	data() {
		const sharingInfo: ISharingInfo = {
			salutation:
				'Hey!\n\nIch habe gerade ein interessantes Jobangebot gefunden. Wäre das etwas für dich?\n',
			link: '',
			jobName: '',
			companyName: '',
			details: '',
			targetObjId: '',
			targetType: 'job'
		};
		const observer = undefined as undefined | IntersectionObserver;
		const cordova = process.env.cordova as string | null;
		const recentJobSearches = [] as IRecentJobsearch[] | [];
		const remuneration = undefined as number | undefined;
		const period: GNCPeriod = 3;

		return {
			recentJobSearches,
			mapState: 'image',
			mode: 'sendjob',
			cordova,
			observer,
			showBar: false,
			faqSection: faqData.faq,
			showGnc: false,
			showMap: false,
			showUSP: false,
			revealFullText: false,
			sharingInfo,
			remuneration,
			period,
			piniaAvailable
		};
	},
	computed: {
		topLevelDomain(): string {
			return piniaAvailable(this.$nuxt, '$userRootStore')
				? this.$nuxt.context.$userRootStore?.topLevelDomain
				: this.$store?.state?.topLevelDomain || 'at';
		},
		jobFields(): { value: string }[] | undefined {
			return this.job?.fields.map(field => ({
				value: field.name
			}));
		},
		requiredQuestions(): IAPIJobQuestion[] | undefined {
			return this.job?.questions?.filter(q => q?.required === true);
		},
		optionalQuestions(): IAPIJobQuestion[] | undefined {
			return this.job?.questions?.filter(q => q?.required === false);
		},
		locations(): IAPILocation[] {
			return [
				{
					latitude: this.job.location?.latitude,
					longitude: this.job.location?.longitude,
					name: this.job.location?.name
				}
			];
		},
		contactPerson(): IAPIOwnerSnippet | undefined {
			return this.job?.owners?.[0];
		},
		contactPersonText(): string {
			return this.contactPerson?.general?.gender === 'female'
				? 'Deine Ansprechpartnerin'
				: 'Dein Ansprechpartner';
		},
		applyTime(): string {
			let totalquestioncount = 0;
			if (this.job.questions?.length) {
				totalquestioncount += this.job.questions.length;
			}
			if (this.job.requiredFields?.length > 0) {
				totalquestioncount += this.job.requiredFields.length;
			}
			return Math.round((totalquestioncount * 20) / 60) <= 1
				? 'innerhalb einer Minute'
				: `ca. ${Math.round((totalquestioncount * 20) / 60)} Minuten`;
		},
		hasRequiredFields(): boolean {
			return this.job?.requiredFields?.some(
				rqf => (rqf as any).type === 'EDUCATIONLEVEL' || (rqf as any).type === 'JOBLEVEL'
			);
		},
		requiredYesNoQuestions(): any[] {
			return (
				this.job?.requiredFields?.filter(
					rqf => (rqf as any).type === 'YES_NO' && (rqf as any).required === true
				) || []
			);
		},
		optionalYesNoQuestions(): any[] {
			return (
				this.job?.requiredFields?.filter(
					rqf => (rqf as any).type === 'YES_NO' && (rqf as any).required !== true
				) || []
			);
		},
		linkToApply(): string {
			return `/apply/${this.job.jobNr}`;
		},
		jobRegion() {
			return this.job?.internal?.region;
		},
		addressRegion(): string | undefined {
			return this.job.location?.county?.toUpperCase() || this.job.internal?.region?.toUpperCase();
		},
		selectedCandidate(): boolean {
			return !!this.job?.selectedCandidateInfo;
		},
		activeSourcingMode(): boolean {
			return !!(this.loggedIn && this.job?.selectedCandidateInfo?.status === 'activesourced');
		},
		isInContactWithCompany(): boolean {
			return !!(this.job?.selectedCandidateInfo?.status === 'incontact');
		},
		hasRejectedContactRequest(): boolean {
			return !!(this.job?.selectedCandidateInfo?.status === 'request-rejected');
		},
		personalCompanyText(): string {
			return this.job?.personalCompanyText || '';
		},
		companyLogo(): string {
			return this.job?.company?.logos?.small || '';
		},
		isATSJob(): boolean {
			return !!(this.job?.selectedCandidateInfo?.applicationTarget === 'application');
		},
		showExtraFunctionality() {
			return (
				!this.job.selectedCandidateInfo?.status ||
				(this.job.selectedCandidateInfo?.status && this.isMatchView)
			);
		},
		popupHandler() {
			return this.$refs.popupHandler as any;
		}
	},
	mounted() {
		if (lsTest()) {
			const storage = localStorage.getItem('recent-jobsearches');
			if (typeof storage === 'string') {
				this.recentJobSearches = JSON.parse(storage);
			}
		}
		this.registerIntersectionObserver();
	},
	props: {
		job: { type: Object as PropType<IAPIJobForUser>, required: true },
		additionalJobData: {
			type: Object as PropType<IAPIPageJobDetail['additionalJobData']>,
			default: () => {}
		},
		additionalSettings: {
			type: Object as PropType<IAPIPageJobDetail['additionalSettings']>,
			default: () => {}
		},
		jobOverviewLps: {
			type: Array as PropType<IAPIPageJobDetail['additionalSettings'][]>,
			default: () => []
		},
		similarJobs: { type: Array as PropType<IAPIJobPreviewForUser[]>, default: () => [] },
		jobField: { type: String, required: false },
		jobIsSaved: { type: Boolean, default: false },
		singleJobView: { type: Boolean, default: false },
		showApplyButton: { type: Boolean, default: true },
		showSaveButton: { type: Boolean, default: false },
		showDeleteButton: { type: Boolean, default: false },
		isMatchView: { type: Boolean, default: false },
		isPwa: { type: Boolean, default: false },
		loggedIn: { type: Boolean, default: false },
		savedMatches: { type: Array as PropType<IAPIMatchForJobSeekerListEntry[]>, required: false },
		gncCalculation: { type: Object, required: false },
		gncClosed: { type: Boolean, required: false },
		userImage: { type: String, required: false },
		region: { type: String, required: false },
		fromRoute: { type: String as PropType<'startpage' | 'gnc' | 'default'>, default: 'default' },
		showEducation: { type: Boolean, default: false }
	},
	watch: {
		job: [
			{
				handler: 'onJobChanged'
			}
		]
	}
});
